import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import tryToPlayVideo from 'utils/tryToPlayVideo';
import { joinClasses } from 'utils/utils';
import { getPages } from 'components/editor/movies/utils';

import Button from 'components/button/button';
import HTML from 'components/core/html/html';
import HomeEditor from 'components/editor/movies/home/home';
import SelectableSection from 'components/editor/selectableSection';
import LinkField from 'components/editor/settings/linkField';
import SimpleField from 'components/editor/settings/simpleField';
import Toggle from 'components/toggle/toggle';

import { getDefaultButtons } from 'src_overrides/studios/universal-pictures-cms/utils/homeButtons';

import s from 'components/editor/movies/home/standard/homeStandard.sss';

export default class StandardHomeEditor extends HomeEditor {
	s = s;

	constructor(props) {
		super(props);

		this.setState({
			scrolled: false,
		});

		this.renderButton = this.renderButton.bind(this);
		this.onResetButtonsClick = this.onResetButtonsClick.bind(this);
		this.onScroll = this.onScroll.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();

		// Find the scroll container by traversing up from the wrapper element
		let scrollContainer = this.base.parentElement;
		while (scrollContainer && scrollContainer.scrollHeight <= scrollContainer.clientHeight) {
			scrollContainer = scrollContainer.parentElement;
		}

		if (scrollContainer) {
			this.scrollContainer = scrollContainer;
			scrollContainer.addEventListener('scroll', this.onScroll);
		}
	}

	componentWillUnmount() {
		super.componentWillUnmount?.();

		if (this.scrollContainer) {
			this.scrollContainer.removeEventListener('scroll', this.onScroll);
		}
	}

	async onResetButtonsClick() {
		const text = 'This will reset the buttons to the defaults.';
		const ok = await confirm(text, { title: `Reset to defaults?`, danger: true, button: 'Reset' });
		if (!ok) return;

		this.resetButtonsToDefault();
	}

	onScroll() {
		if (!this.scrollContainer) return;

		const scrollTop = this.scrollContainer.scrollTop;
		const shouldHide = scrollTop > 100;

		if (shouldHide !== this.state.scrolled) {
			this.setState({ scrolled: shouldHide });
		}
	}

	getDefaultCtaButtons() {
		return getDefaultButtons(this.movie, this.movie.status, this.state.thundrData?.copy?.baseCopy);
	}

	playVideo(e) {
		tryToPlayVideo(e.target);
	}

	resetButtonsToDefault() {
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/button1`, null);
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/button2`, null);
	}

	renderTT() {
		const base = super.renderTT();
		
		if (base?.attributes?.class) {
			const ttSrc = this.getTT();
			const { scrolled } = this.state;

			if (ttSrc) {
				base.attributes.class = joinClasses(base.attributes.class, !scrolled && this.s.visible, scrolled && this.s.hide);
			}
		}

		return base;
	}

	renderButton(button, index) {
		if (!button || !button.enabled) {
			return;
		}

		const isPrimary = button.key === 'button1' || index === 0;
		const buttonClass = isPrimary ? this.s.buttonPrimary : this.s.buttonSecondary;

		return (
			<SelectableSection {...this.props} section={`/pages/home/${button.key}`} class={this.s.buttonSection} key={button.key}>
				<div class={joinClasses(this.s.button, buttonClass)}>
					<HTML class={this.s.buttonLabel} markdown inline>
						{button.label}
					</HTML>
				</div>
			</SelectableSection>
		);
	}

	renderHeroContent() {
		const homeData = this.movie?.pages?.home;
		const defaultButtons = this.getDefaultCtaButtons();

		const buttons = ['button1', 'button2'].map(key => {
			const data = homeData?.[key] || defaultButtons?.[key];
			return data && { key, ...data };
		}).filter(Boolean);

		return (
			<div class={joinClasses(this.s.buttons, this.s.visible, this.s[homeData?.buttonLayout || 'row'])} key="buttons">
				{buttons.map(this.renderButton)}
			</div>
		);
	}

	isEdited(current, defaultObj) {
		if (current === defaultObj) return false;
		if (!current || !defaultObj) return false;

		const keysA = Object.keys(current);
		for (const key of keysA) {
			if (current[key] !== defaultObj[key]) return true;
		}

		return false;
	}

	toggleButton(key, e) {
		let data = this.movie?.pages?.home?.[key] || this.getDefaultCtaButtons()[key] || {};
		if (data.toJS) data = data.toJS();
		data = structuredClone(data);
		data.enabled = e.target.checked;
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/${key}`, data);
	}

	ensureButtonOverride(key) {
		if (this.movie?.pages?.home?.[key]) return;
		const defaultValue = this.getDefaultCtaButtons()[key];
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/${key}`, defaultValue);
	}

	async onResetButtonClick(button) {
		const buttonName = button === 'button1' ? 'primary' : 'secondary';
		const text = `This will reset the ${buttonName} button to the default.`;
		const ok = await confirm(text, { title: `Reset to default?`, danger: true, button: 'Reset' });
		if (!ok) return;

		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/${button}`, null);
		this.props.select(`/pages/home`);
	}

	renderButtonSettings(button) {
		const buttonName = button === 'button1' ? 'Primary' : 'Secondary';
		const buttonPath = `${this.props.dataRoot}/content/pages/home/${button}`;
		const buttonDataRaw = this.movie?.pages?.home?.[button];
		const buttonDefaultData = this.getDefaultCtaButtons()[button];
		const buttonData = buttonDataRaw || buttonDefaultData;
		const ensureOverride = () => this.ensureButtonOverride(button);

		const linkOptions = [
			...getPages(this.props.data),
			{ destination: '@calendarreminder', name: 'Save to Calendar', disabled: false },
		];

		return (
			<div class={this.s.itemEditor}>
				<div class={this.s.itemGroup}>
					<h3>{buttonName} button</h3>
					<SimpleField dark path={`${buttonPath}/label`} label={`${buttonName} button text`} defaultValue={buttonData.label} onInput={ensureOverride} />
					<LinkField
						dark
						label={`${buttonName} button link`}
						path={`${buttonPath}/path`}
						defaultValue={buttonData.path}
						options={linkOptions}
						onInput={ensureOverride}
					/>
					<Button
						dark text danger
						class={this.s.resetButton}
						onClick={() => this.onResetButtonClick(button)}
						disabled={!this.isEdited(buttonData, buttonDefaultData)}
					>
						Reset button
					</Button>
				</div>
			</div>
		);
	}

	renderButtonSummary(key) {
		const dataRaw = this.movie?.pages?.home?.[key];
		const defaultData = this.getDefaultCtaButtons()[key];
		const data = dataRaw || defaultData;

		// Check if label or path has been edited from defaults
		// Only show edited if the values actually differ from defaults
		const labelEdited = dataRaw?.label && dataRaw.label !== defaultData.label;
		const pathEdited = dataRaw?.path && dataRaw.path !== defaultData.path;
		const isEdited = labelEdited || pathEdited;

		return (
			<Toggle
				dark small
				class={this.s.buttonToggle}
				checked={data?.enabled}
				onInput={(e) => this.toggleButton(key, e)}
			>
				<div class={this.s.item}>
					<span class={this.s.label}>{data?.label}</span>
					{isEdited && <span class={this.s.edited}>{'(edited)'}</span>}
					<button class={this.s.edit} disabled={!data?.enabled} onClick={() => this.props.select(`/pages/home/${key}`)}>
						Edit
					</button>
				</div>
			</Toggle>
		);
	}

	renderHeroContentSettings() {
		const homeContent = this.movie?.pages?.home;
		const defaultButtonData = this.getDefaultCtaButtons();
		const buttonsEdited = this.isEdited(homeContent?.button1, defaultButtonData.button1) || this.isEdited(homeContent?.button2, defaultButtonData.button2);

		return (
			<div class={this.s.itemGroup}>
				<h3>Buttons</h3>
				{this.renderButtonSummary('button1')}
				{this.renderButtonSummary('button2')}
				<Button class={this.s.resetButton} dark text danger disabled={!buttonsEdited} onClick={this.onResetButtonsClick}>
						Reset buttons
				</Button>
			</div>
		);
	}

	renderSettings() {
		const activeElement = this.props.activeElement;
		const nav = [
			{ label: 'Home', path: `/` },
			{ label: 'Hero', path: `/pages/home/hero` }
		];

		if (activeElement === '/pages/home/button1' || activeElement === '/pages/home/button2') {
			const button = activeElement.split('/').pop();
			const buttonName = button === 'button1' ? 'Primary' : 'Secondary';
			nav.push({ label: `${buttonName} button`, path: activeElement });

			return (
				<div class={this.s.settings}>
					{this.renderSettingsNavigation(nav)}
					{this.renderButtonSettings(button)}
				</div>
			);
		}

		return super.renderSettings();
	}
}
